import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle, XCircle, Loader2 } from 'lucide-react'
import { api } from '../services/api'

export function AcceptInvitation() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')
  const [errorDetails, setErrorDetails] = useState('')

  useEffect(() => {
    const token = searchParams.get('token')

    if (!token) {
      setStatus('error')
      setMessage('Token de convite inválido')
      setErrorDetails('O link do convite está incompleto ou corrompido.')
      return
    }

    acceptInvitation(token)
  }, [searchParams])

  const acceptInvitation = async (token: string) => {
    try {
      setStatus('loading')

      // Call backend to accept invitation
      const response = await api.post('/auth/accept-invitation', { token })

      setStatus('success')
      setMessage('Convite aceito com sucesso!')

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate('/login', {
          state: {
            message: 'Convite aceito! Faça login para acessar sua conta.',
            email: response.data.email
          }
        })
      }, 2000)

    } catch (error: any) {
      setStatus('error')
      setMessage('Erro ao aceitar convite')

      if (error.response?.status === 404) {
        setErrorDetails('Convite não encontrado ou já foi utilizado.')
      } else if (error.response?.status === 410) {
        setErrorDetails('Este convite expirou. Solicite um novo convite ao administrador.')
      } else if (error.response?.data?.message) {
        setErrorDetails(error.response.data.message)
      } else {
        setErrorDetails('Ocorreu um erro ao processar seu convite. Tente novamente.')
      }
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 border border-gray-200 dark:border-gray-700">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
              <span className="text-2xl font-bold text-white">NGC</span>
            </div>
          </div>

          {/* Status Icon */}
          <div className="flex justify-center mb-4">
            {status === 'loading' && (
              <Loader2 className="w-16 h-16 text-blue-500 animate-spin" />
            )}
            {status === 'success' && (
              <CheckCircle className="w-16 h-16 text-green-500" />
            )}
            {status === 'error' && (
              <XCircle className="w-16 h-16 text-red-500" />
            )}
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-center text-gray-900 dark:text-white mb-2">
            {status === 'loading' && 'Processando convite...'}
            {status === 'success' && 'Convite aceito!'}
            {status === 'error' && 'Erro no convite'}
          </h1>

          {/* Message */}
          <p className="text-center text-gray-600 dark:text-gray-300 mb-4">
            {message}
          </p>

          {/* Error Details */}
          {status === 'error' && errorDetails && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-800 dark:text-red-200">{errorDetails}</p>
            </div>
          )}

          {/* Success Details */}
          {status === 'success' && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
              <p className="text-sm text-green-800 dark:text-green-200">
                Você será redirecionado para a página de login em alguns segundos...
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col gap-2 mt-6">
            {status === 'error' && (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
                >
                  Ir para Login
                </button>
                <button
                  onClick={() => window.location.reload()}
                  className="w-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 font-medium py-2.5 px-4 rounded-lg transition-colors"
                >
                  Tentar Novamente
                </button>
              </>
            )}

            {status === 'success' && (
              <button
                onClick={() => navigate('/login')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2.5 px-4 rounded-lg transition-colors"
              >
                Ir para Login Agora
              </button>
            )}
          </div>

          {/* Help Text */}
          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6">
            Problemas com o convite?{' '}
            <a href="mailto:suporte@ngcmedical.com" className="text-blue-600 dark:text-blue-400 hover:underline">
              Entre em contato
            </a>
          </p>
        </div>
      </div>
    </div>
  )
}
